/* eslint-disable @typescript-eslint/no-explicit-any */
import { Fragment, MouseEvent } from 'react';
import { line } from 'd3-shape';
import {
  regressionLinear,
  regressionExp,
  regressionLog,
  regressionQuad,
  regressionPoly,
  regressionLoess,
} from 'd3-regression';
import { useTooltip } from '@nivo/tooltip';
import { curveFromProp, CurveFactoryId } from '@nivo/core';
import { labelValuesStr } from '../formatCharts/handleCustomValue';

interface styleConfigProps {
  LineFormatControl: CurveFactoryId;
  LineColorControl: string;
  LineWidthControl: number;
  EnablePointsControl: boolean;
  PointsSizeControl: number;
  PointsBorderWidthControl: number;
  PointsColorControl: string;
  EnableLabelPointsControl: boolean;
  LabelsFontSizeControl: { value: number };
  LabelsFontFamilyControl: { value: string };
  LabelTranslateXPoints: number;
  LabelTranslateYPoints: number;
  LabelRotationPointsControl: number;
  LabelTextColorPointsControl: string;
  EnableLabelControl: { checked: boolean };
  GroupModeControl: string;
}

interface globalConfigProps {
  useGlobalFont: boolean;
  fontLabel: string;
}

interface ExtraLineProps {
  bars: Record<string, any>[];
  rawBars: Record<string, any>[];
  dataKey: string;
  lineType: string;
  yScale: CallableFunction;
  tooltip: CallableFunction;
  styleConfig: styleConfigProps;
  globalConfig: globalConfigProps;
}

const statisticalRegression = (op: CallableFunction, bars: Record<string, any>[]) => (
  op()
    .x((bar: Record<string, any>) => bar.x)
    .y((bar: Record<string, any>) => bar.y))(bars)
  .map(([x, y]: Record<string, any>[]) => ({
    width: bars[0].width, y, x,
  }));

const lineGenerator = (
  lineType: string,
  bars: Record<string, any>[],
  dataKey: string,
  styleConfig: styleConfigProps,
  yScale: CallableFunction,
): string | undefined => {
  const dtBars = bars.map((bar): Record<string, number> => ({
    x: bar.x ?? 0,
    y: bar.data.data[dataKey] ?? 0,
    width: bar.width,
  }));
  const linePath = line<Record<string, number>>()
    .x((b) => (b.x + b.width / 2))
    .y((b) => yScale(b.y))
    .curve(curveFromProp(styleConfig.LineFormatControl));
  switch (lineType) {
    case 'RegressionLinear':
      return linePath(
        statisticalRegression(regressionLinear, dtBars),
      ) ?? undefined;
    case 'RegressionExponential':
      return linePath(
        statisticalRegression(regressionExp, dtBars),
      ) ?? undefined;
    case 'RegressionLogarithmic':
      return linePath(
        statisticalRegression(regressionLog, dtBars),
      ) ?? undefined;
    case 'RegressionQuadratic':
      return linePath(
        statisticalRegression(regressionQuad, dtBars),
      ) ?? undefined;
    case 'RegressionPolynomial':
      return linePath(
        statisticalRegression(regressionPoly, dtBars),
      ) ?? undefined;
    case 'RegressionLOESS':
      return linePath(
        statisticalRegression(regressionLoess, dtBars),
      ) ?? undefined;
    default:
      return linePath(dtBars) ?? undefined;
  }
};

const ExtraLine = ({
  bars, rawBars, dataKey, lineType, yScale, tooltip, styleConfig, globalConfig,
}: ExtraLineProps) => {
  const tip = useTooltip();

  const handleTooltip = (e: MouseEvent, datum: Record<string, any>) => (
    tip.showTooltipFromEvent(tooltip({
      ...datum.data, color: datum.color, offBar: true, isTotal: true,
    }), e)
  );

  return (
    <>
      <path
        d={lineGenerator(lineType, bars, dataKey, styleConfig, yScale)}
        fill="none"
        stroke={styleConfig.LineColorControl}
        strokeWidth={styleConfig.LineWidthControl}
        style={{ pointerEvents: 'none' }}
      />
      {bars.map((bar, i) => (
        <Fragment key={bar.key}>
          <g>
            {styleConfig.EnablePointsControl && (
              <circle
                className="extra-line-point"
                cx={bar.x + bar.width / 2}
                cy={yScale(bar.data.data[dataKey] ?? 0)}
                r={styleConfig.PointsSizeControl / 2}
                stroke={styleConfig.LineColorControl}
                strokeWidth={styleConfig.PointsBorderWidthControl}
                fill={styleConfig.PointsColorControl}
                // style={{ pointerEvents: 'none' }}
                onMouseEnter={(e: MouseEvent<SVGCircleElement>) => handleTooltip(e, bar)}
                onMouseMove={(e: MouseEvent<SVGCircleElement>) => handleTooltip(e, bar)}
                onMouseLeave={tip.hideTooltip}
              />
            )}
            {Boolean(styleConfig.EnableLabelPointsControl)
            && !(lineType === 'Pareto' && i === 0)
            && (
              <text
                dx={bar.x + bar.width / 2}
                dy={yScale(bar.data.data[dataKey] ?? 0)}
                fill={styleConfig.LabelTextColorPointsControl}
                fillOpacity="1"
                dominantBaseline="central"
                textAnchor="middle"
                style={{
                  fontSize: `${styleConfig.LabelsFontSizeControl.value}px`,
                  fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontLabel : styleConfig.LabelsFontFamilyControl.value}', Arial`,
                  transform: `translate(${styleConfig.LabelTranslateXPoints}px, ${styleConfig.LabelTranslateYPoints}px) rotate(${styleConfig.LabelRotationPointsControl}deg)`,
                  transformOrigin: 'center center',
                  transformBox: 'fill-box',
                  outlineWidth: '0px',
                  outlineColor: 'transparent',
                  pointerEvents: 'none',
                }}
              >
                <tspan y={-15}>
                  {labelValuesStr(
                    bar.data.data[dataKey] ?? 0,
                    lineType === 'Totals' ? bar.data.data[`${dataKey}2`] ?? 0 : null,
                    styleConfig,
                  )}
                </tspan>
              </text>
            )}
          </g>
        </Fragment>
      ))}
      {/* {styleConfig.GroupModeControl !== 'stacked' && (rawBars ?? bars).map((bar) => (
        <Fragment key={bar.key}>
          <rect
            x={bar.x}
            y={0}
            height={bar.y}
            width={bar.width}
            fill="transparent"
            onMouseEnter={(e: MouseEvent<SVGRectElement>) => handleTooltip(e, bar)}
            onMouseMove={(e: MouseEvent<SVGRectElement>) => handleTooltip(e, bar)}
            onMouseLeave={tip.hideTooltip}
          />
        </Fragment>
      ))} */}
    </>
  );
};

export default ExtraLine;
